import {Component, ElementRef, EventEmitter, HostListener, OnInit, Output, ViewChild} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {Router} from '@angular/router';
import {of} from 'rxjs';
import {debounceTime, switchMap, tap} from 'rxjs/operators';
import {GlobalSearchService} from '../../../../services/search/global-search.service';
import {GlobalSearchResultGroup} from '../../../../dtos/search/global-search-results';
import {SEARCH_TYPE_LABELS} from '../../../../shared/search/search-type-labels';
import {SearchResultType} from '../../../../shared/search/search-result-type';
import {navigateToSearchResult} from '../../../../shared/search/search-navigation';
import {NotificationService} from '../../../../services/common/notification.service';

const MIN_QUERY_LENGTH = 2;

@Component({
  selector: 'app-header-search',
  templateUrl: './header-search.component.html',
  styleUrl: './header-search.component.scss',
  standalone: false
})
export class HeaderSearchComponent implements OnInit {
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  // lets the header hide the other nav items while search takes up the full width
  @Output() expandedChange = new EventEmitter<boolean>();

  expanded = false;
  searchControl = new UntypedFormControl('');
  groups: GlobalSearchResultGroup[] = [];
  isLoading = false;

  readonly typeLabels = SEARCH_TYPE_LABELS;
  readonly minQueryLength = MIN_QUERY_LENGTH;

  constructor(private globalSearchService: GlobalSearchService, private router: Router,
              private notificationService: NotificationService, private elementRef: ElementRef) {
  }

  ngOnInit() {
    this.searchControl.valueChanges
      .pipe(
        tap(value => {
          this.isLoading = String(value ?? '').trim().length >= MIN_QUERY_LENGTH;
        }),
        debounceTime(300),
        // no distinctUntilChanged: collapse() resets the control silently
        switchMap(value => {
          const q = String(value ?? '').trim();
          if (q.length < MIN_QUERY_LENGTH) {
            this.groups = [];
            return of(null);
          }
          return this.globalSearchService.search(q, 5);
        })
      )
      .subscribe({
        next: result => {
          this.isLoading = false;
          this.groups = result?.groups ?? [];
        },
        error: () => {
          this.isLoading = false;
          this.groups = [];
        }
      });
  }


  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.expanded && !this.elementRef.nativeElement.contains(event.target)) {
      this.collapse();
    }
  }

  toggle(event: MouseEvent) {
    event.stopPropagation();
    this.expanded = !this.expanded;
    if (this.expanded) {
      this.expandedChange.emit(true);
      setTimeout(() => this.searchInput?.nativeElement.focus());
    } else {
      this.collapse();
    }
  }

  select(typeOf: string, id: number) {
    if (!navigateToSearchResult(this.router, typeOf, id)) {
      this.notificationService.openSnackBar('Derzeit wird diese Funktion für diese Art nicht unterstützt');
    }
    this.collapse();
  }

  submit() {
    const q = String(this.searchControl.value ?? '').trim();
    if (q) {
      this.router.navigate(['/suche'], {queryParams: {q}});
      this.collapse();
    }
  }

  viewAllInDomain(type: SearchResultType) {
    const q = String(this.searchControl.value ?? '').trim();
    const route = this.typeLabels[type]?.route;
    if (route && q) {
      this.router.navigate([route], {queryParams: {q}});
      this.collapse();
    }
  }

  collapse() {
    this.expanded = false;
    this.expandedChange.emit(false);
    this.isLoading = false;
    this.searchControl.setValue('', {emitEvent: false});
    this.groups = [];
  }
}
